import '../recording/recordingStore';
import { scenarioRecorder } from '../recording/scenarioRecorder';
import { useRecorderStore } from '../recording/useRecorderStore';

const RECORDABLE_CLICK_SELECTOR = [
  '[data-testid]',
  '[aria-label]',
  'button',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  'a[href]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
].join(',');

const FORM_SELECTOR = 'input, textarea, select';
const RECORDER_CONTROL_SELECTOR = [
  '[data-testid="recorder-dock"]',
  '[data-testid="recorder-controls"]',
  '[data-testid^="recorder-"]',
].join(',');

let installed = false;

export function installRecorderUiCapture(): void {
  if (installed || typeof document === 'undefined') {
    return;
  }

  document.addEventListener('click', handleClick, true);
  document.addEventListener('change', handleChange, true);
  installed = true;
}

export function uninstallRecorderUiCapture(): void {
  if (!installed || typeof document === 'undefined') {
    return;
  }

  document.removeEventListener('click', handleClick, true);
  document.removeEventListener('change', handleChange, true);
  installed = false;
}

function handleClick(event: MouseEvent): void {
  if (event.button !== 0) {
    return;
  }

  const element = closestElement(event.target, RECORDABLE_CLICK_SELECTOR);
  if (!element || shouldIgnore(element)) {
    return;
  }

  const target = targetName(element);
  if (!target) {
    return;
  }

  const index = scenarioRecorder.recordClick(target, elementDetails(element, event));
  refreshUi(index);
}

function handleChange(event: Event): void {
  const element = closestElement(event.target, FORM_SELECTOR);
  if (!element || shouldIgnore(element)) {
    return;
  }

  const target = targetName(element);
  if (!target) {
    return;
  }

  let index: number | null = null;
  if (element instanceof HTMLSelectElement) {
    index = scenarioRecorder.recordSelection(target, {
      value: element.value,
      label: element.selectedOptions[0]?.textContent?.trim() || undefined,
    });
  } else if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
    index = scenarioRecorder.recordSelection(target, { checked: element.checked, value: element.value });
  } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    index = scenarioRecorder.recordInputChange(target, element.value);
  }

  refreshUi(index);
}

function closestElement(target: EventTarget | null, selector: string): Element | null {
  if (!(target instanceof Element)) {
    return null;
  }
  return target.closest(selector);
}

function shouldIgnore(element: Element): boolean {
  return !!element.closest(RECORDER_CONTROL_SELECTOR);
}

function targetName(element: Element): string | null {
  const testId = element.getAttribute('data-testid');
  if (testId) {
    return testId;
  }

  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    return `aria:${normalizeText(ariaLabel)}`;
  }

  const id = element.getAttribute('id');
  if (id) {
    return `id:${id}`;
  }

  const name = element.getAttribute('name');
  if (name) {
    return `name:${name}`;
  }

  const text = normalizeText(element.textContent ?? '');
  return text ? `${element.tagName.toLowerCase()}:${text}` : null;
}

function elementDetails(element: Element, event: MouseEvent): Record<string, unknown> | undefined {
  const details: Record<string, unknown> = {
    tag: element.tagName.toLowerCase(),
  };

  const role = element.getAttribute('role');
  if (role) {
    details.role = role;
  }

  if (element instanceof HTMLInputElement && element.type) {
    details.type = element.type;
  }

  const text = normalizeText(element.textContent ?? '');
  if (text && !element.getAttribute('data-testid')) {
    details.text = text;
  }

  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    details.modifiers = {
      alt: event.altKey,
      ctrl: event.ctrlKey,
      meta: event.metaKey,
      shift: event.shiftKey,
    };
  }

  return Object.keys(details).length > 0 ? details : undefined;
}

function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, 120);
}

function refreshUi(index: number | null): void {
  if (index !== null) {
    useRecorderStore.getState().refresh();
  }
}
